import React, { useMemo, useCallback, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import { CiExport } from 'react-icons/ci';
import { useGetPrescriptionQuery } from '@@/services/prescription';
import { useViewDocumentQuery } from '@@/services/document';
import { useViewPrescriptionPDFQuery } from '@@/services/prescription';
import Prescription from 'front-ps/lib/src/components/PDF/Prescription';
import { PrescriptionTypes } from 'front-ps/lib/src/constants/prescription';

const PrescriptionPage: React.FC = () => {
  const { prescriptionId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const os = queryParams.get('os');

  const { data: prescription, isLoading: isLoadingPrescription } =
    useGetPrescriptionQuery(prescriptionId || '', {
      skip: !prescriptionId,
    });

  const { data: pdfUrl, isLoading: isLoadingPDF } = useViewPrescriptionPDFQuery(
    prescriptionId || '',
    {
      skip: !prescriptionId || prescription?.type !== PrescriptionTypes.BIZONE,
    },
  );

  const { data: signature, isLoading: isLoadingSignature } =
    useViewDocumentQuery(
      prescription?.practitioner?.signatureDocumentId || '',
      {
        skip: !prescription?.practitioner?.signatureDocumentId,
      },
    );

  const isLoading = isLoadingPrescription || isLoadingPDF || isLoadingSignature;

  const prescriptionPDF = useMemo(
    () => (
      <Prescription
        prescription={prescription}
        patient={prescription?.patient}
        practitioner={prescription?.practitioner}
        signature={signature}
      />
    ),
    [prescription, signature],
  );

  const handleDownload = useCallback(async () => {
    if (!prescription) {
      return;
    }

    if (prescription.type !== PrescriptionTypes.BIZONE && !prescriptionPDF) {
      return;
    }

    if (prescription.type === PrescriptionTypes.BIZONE && !pdfUrl) {
      return;
    }

    const url =
      prescription.type === PrescriptionTypes.BIZONE
        ? pdfUrl || ''
        : URL.createObjectURL(await pdf(prescriptionPDF).toBlob());

    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = `Ordonnance-${prescription.patient?.firstName}-${prescription.patient?.lastName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (prescription.type !== PrescriptionTypes.BIZONE) {
      URL.revokeObjectURL(url);
    }
  }, [prescription, prescriptionPDF, pdfUrl]);

  const downloadButton = useMemo(() => {
    return (
      <button
        className="bg-cyan-400 rounded-lg p-2 flex flex-row items-center text-white h-fit"
        onClick={handleDownload}
      >
        <CiExport size={22} />
        <span className="ml-1">Exporter</span>
      </button>
    );
  }, [handleDownload]);

  useEffect(() => {
    if (os) {
      handleDownload();
    }
  }, [os, handleDownload]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full">
        <p className="mx-auto text-3xl font-main text-main">
          Chargement en cours...
        </p>
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="flex items-center justify-center w-full">
        <p className="mx-auto text-3xl font-main text-main">
          Désolé, nous n'avons pas pu trouver votre ordonnance
        </p>
      </div>
    );
  }

  if (os) {
    return (
      <div className="w-full flex flex-col items-center justify-center">
        {downloadButton}
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <PDFViewer className="h-full w-full">{prescriptionPDF}</PDFViewer>

      <div className="block sm:hidden absolute right-4 top-8">
        {downloadButton}
      </div>
    </div>
  );
};

export default PrescriptionPage;
